'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import {
  Dialog as RACDialog,
  DialogTrigger as RACDialogTrigger,
  Heading as RACHeading,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { XButton } from '../internal/x-button/x-button.js';
import type { AriaLabelingProps, CommonProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface DrawerRootProps {
  /** The drawer trigger with its associated drawer. Provide the trigger as the first child, and the drawer as the second child. */
  children: ReactNode;
  /** Whether the drawer is open (controlled). */
  isOpen?: boolean;
  /** Whether the drawer is open by default (uncontrolled). */
  defaultOpen?: boolean;
  /** Handler that is called when the drawer's open state changes. */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Encapsulates a drawer trigger and its associated drawer.
 * The trigger can be any Cimpress UI button, and the drawer will be displayed when the trigger is activated.
 */
export function DrawerRoot(props: DrawerRootProps) {
  return <RACDialogTrigger {...props} />;
}

DrawerRoot.displayName = 'DrawerRoot';

export interface DrawerRenderProps {
  /** Closes the drawer when called. */
  close: () => void;
}

export interface DrawerProps extends CommonProps, AriaLabelingProps {
  /** The contents of the drawer. A function may be provided to access a function to close the drawer. */
  children: ReactNode | ((renderProps: DrawerRenderProps) => ReactNode);
  /** The title of the drawer. */
  title?: string;
  /**
   * The size of the drawer. This prop is ignored on small devices - drawers on small devices will always take up all available width.
   * @default 'small'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether the drawer can be closed by clicking the close button, clicking outside of the drawer, or pressing the Escape key.
   * When `false`, the drawer can only be closed programmatically.
   * @default true
   */
  isDismissible?: boolean;
  /**
   * Whether the drawer is open (controlled).
   * If using `DrawerRoot`, this prop has no effect - provide `isOpen` to `DrawerRoot` instead.
   */
  isOpen?: boolean;
  /**
   * Whether the drawer is open by default (uncontrolled).
   * If using `DrawerRoot`, this prop has no effect - provide `defaultOpen` to `DrawerRoot` instead.
   */
  defaultOpen?: boolean;
  /**
   * Handler that is called when the drawer's open state changes.
   * If using `DrawerRoot`, this prop has no effect - provide `onOpenChange` to `DrawerRoot` instead.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * The placement of the drawer.
   * @default 'end'
   */
  UNSTABLE_placement?: 'start' | 'end';
}

function Drawer(
  {
    children,
    title,
    size = 'small',
    UNSAFE_className,
    UNSAFE_style,
    isDismissible = true,
    isOpen,
    defaultOpen,
    onOpenChange,
    UNSTABLE_placement: placement = 'end',
    ...props
  }: DrawerProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!title && !ariaLabel && !ariaLabelledBy) {
      console.warn('Drawer requires one of title / aria-label / aria-labelledby for accessibility');
    }
  }, [title, ariaLabel, ariaLabelledBy]);

  const messages = useLocalizedMessages('common');

  return (
    <RACModalOverlay
      className="cim-drawer-overlay"
      isDismissable={isDismissible}
      isKeyboardDismissDisabled={!isDismissible}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      data-cim-style-root
      data-placement={placement}
    >
      <RACModal
        className={clsx('cim-drawer', UNSAFE_className)}
        style={UNSAFE_style}
        data-size={size}
        data-placement={placement}
      >
        <RACDialog {...props} ref={ref} className="cim-drawer-dialog">
          {(renderProps) => (
            <>
              <div className="cim-drawer-header">
                {title && (
                  <RACHeading
                    slot="title"
                    className={clsx('cim-drawer-title', textStyle({ variant: 'title-5', tone: 'base' }))}
                  >
                    {title}
                  </RACHeading>
                )}
                {isDismissible && <XButton aria-label={messages.format('dismiss')} slot="close" />}
              </div>
              {typeof children === 'function' ? children(renderProps) : children}
            </>
          )}
        </RACDialog>
      </RACModal>
    </RACModalOverlay>
  );
}

/**
 * Displays an overlay element which blocks interaction with outside elements.
 *
 * See [drawer usage guidelines](https://ui.cimpress.io/components/drawer/).
 */
const _Drawer = forwardRef(Drawer, 'Drawer');

export { _Drawer as Drawer };

export interface DrawerBodyProps {
  /** The content to render within the drawer. */
  children: ReactNode;
}

/** Renders content within `Drawer`. */
export function DrawerBody(props: DrawerBodyProps) {
  return <div {...props} className="cim-drawer-body" />;
}

DrawerBody.displayName = 'DrawerBody';

export interface DrawerActionsProps {
  /** Actions that should be available in the drawer. */
  children: ReactNode;
}

/** Renders actions within `Drawer`. */
export function DrawerActions({ children, ...props }: DrawerActionsProps) {
  return (
    <div {...props} className="cim-drawer-actions">
      {children}
    </div>
  );
}

DrawerActions.displayName = 'DrawerActions';
